import React from "react";
import { useTranslation } from "react-i18next";
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { Question, Questionnaire } from "./models";
import RequiredAsterisk from "./RequiredAsterisk";
import { QuestionsView } from "./QuestionsView";
import { translateReactNode } from "../../../../utils/translateReactNode";

const MultipleChoiseView = ({ question, questionnaire, onChange }: {question: Question, questionnaire: Questionnaire, onChange: (Question: Question) => void}) => {
  const { t } = useTranslation(questionnaire.cohort);
  let { options, value } = question;
  options = options || [];
  value = value || '';

  const handleCheckboxChange = (optionKey: string) => {
    question.value = optionKey;
    onChange(question);
  };

  const handleOnQuestionnaireChange = (optionKey: string, followUpQuestions: Question[]) => {
    // update the follow-up questions of the selected option
    question.options = options.map(option => {
      if (option.key === optionKey) {
        option.followUpQuestions = followUpQuestions;
      }

      return option;
    });

    // update the question
    onChange(question);
  };

  // Clear the follow up questions if the value changes
  options.forEach(option => {
    if (option.key !== value) {
      option.followUpQuestions?.forEach(followUpQuestion => {
        followUpQuestion.value = followUpQuestion.defaultValue;
      });
    }
  });

  return (
    <FormControl component="fieldset">
      {question.content && (
        <Box sx={{ mb: 1 }}>
          {translateReactNode(question.content, questionnaire.cohort)}
        </Box>
      )}
      <FormLabel component="legend">
        <Typography variant="h6" component="div">
          {t(question.question)}
          {question.required && <RequiredAsterisk />}
        </Typography>
        {question.hint && (
          <Typography variant="body2" color="textSecondary">
            {t(question.hint)}
          </Typography>
        )}
      </FormLabel>
      <RadioGroup onChange={(event) => handleCheckboxChange(event.target.value)} value={value} >
        {options.map((option, index) => (
          <React.Fragment key={index}>
            <FormControlLabel
              value={option.key}
              control={<Radio />}
              label={t(option.displayName)}
            />

            {value === option.key && option.followUpQuestions && (
              // If the radio button is selected and there are follow-up questions
              <div style={{ marginLeft: '2rem' }}>
                <QuestionsView
                  questions={option.followUpQuestions}
                  questionnaire={questionnaire}
                  onChange={(followUpQuestions) => handleOnQuestionnaireChange(option.key, followUpQuestions)} />
              </div>
            )}

          </React.Fragment>
        ))}
      </RadioGroup>
    </FormControl>
  );
};

export default MultipleChoiseView;
